import { CommonResponse } from "@boi/shared-models";
import { Injectable } from "@nestjs/common";
import { DataSource, Repository } from "typeorm";
import { AiGeneratedQpService } from "../ai-generated-qp/ai-generated-qp.service";
import { QuestionPapers } from "../ai-generated-qp/entities/question-papers";
import { InjectRepository } from "@nestjs/typeorm";

@Injectable()
export class DashboardService {
  constructor(
    private readonly dataSource: DataSource,
    @InjectRepository(QuestionPapers)
    private readonly qpRepo: Repository<QuestionPapers>
  ) {}

  async getAbilityDistribution(
    classNum: number,
    subjectId?: number
  ): Promise<CommonResponse> {
    try {
      let query = `
        SELECT
          a.id,
          a.type AS name,
          COUNT(cq.id) AS total,
          CASE
            WHEN aq.total_ability_questions = 0 THEN 0
            ELSE ROUND((COUNT(cq.id) * 100.0) / aq.total_ability_questions)
          END AS percentage
        FROM abilities a
        CROSS JOIN (
          SELECT COUNT(cq2.id) AS total_ability_questions
          FROM createquestion cq2
          INNER JOIN abilities a2 ON a2.id = cq2.ability
          WHERE cq2.class = ?
            AND cq2.is_active = 1
            AND a2.status = 1
            ${subjectId ? "AND cq2.subject = ?" : ""}
        ) aq
        LEFT JOIN createquestion cq
          ON cq.ability = a.id
          AND cq.class = ?
          AND cq.is_active = 1
          ${subjectId ? "AND cq.subject = ?" : ""}
        WHERE a.status = 1
        GROUP BY a.id, a.type, aq.total_ability_questions
        ORDER BY a.id;
      `;

      const params: (number | undefined)[] = subjectId
        ? [classNum, subjectId, classNum, subjectId]
        : [classNum, classNum];

      const result = await this.dataSource.query(query, params);

      return new CommonResponse(
        true,
        200,
        "Ability distribution fetched successfully",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error retrieving ability distribution",
        error.message
      );
    }
  }

  async getOverallDifficultyMix(classNum: number): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(
        `
        SELECT
          LOWER(
            CASE
              WHEN LOWER(c.complexity) = 'difficult' THEN 'hard'
              ELSE c.complexity
            END
          ) AS level,
          COUNT(cq.id) AS total
        FROM complexity c
        LEFT JOIN createquestion cq
          ON cq.complexity = c.id
          AND cq.is_active = 1
          AND cq.class = ?
        GROUP BY
          LOWER(
            CASE
              WHEN LOWER(c.complexity) = 'difficult' THEN 'hard'
              ELSE c.complexity
            END
          )
        ORDER BY FIELD(level, 'easy', 'moderate', 'hard');
      `,
        [classNum]
      );

      const totalQuestions = result.reduce(
        (sum, row) => sum + (Number(row.total) || 0),
        0
      );
      const enriched = result.map((row) => ({
        ...row,
        percentage:
          totalQuestions > 0
            ? Math.round((Number(row.total) * 100) / totalQuestions)
            : 0,
      }));

      return new CommonResponse(
        true,
        200,
        "Overall difficulty mix fetched successfully",
        enriched
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error retrieving overall difficulty mix",
        error.message
      );
    }
  }

  async getSubAbilityDistribution(classNum: number): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(
        `
        SELECT
          at.id,
          at.types AS name,
          a.id AS ability_id,
          a.type AS ability_name,
          COUNT(cq.id) AS total
        FROM ability_types at
        INNER JOIN abilities a ON a.id = at.ability_id
        LEFT JOIN createquestion cq
          ON cq.ability_type = at.id
          AND cq.class = ?
          AND cq.is_active = 1
        WHERE at.status = 1
          AND a.status = 1
        GROUP BY at.id, at.types, a.id, a.type
        ORDER BY COUNT(cq.id) DESC, at.id ASC;
      `,
        [classNum]
      );

      return new CommonResponse(
        true,
        200,
        "Sub-ability distribution fetched successfully",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error retrieving sub-ability distribution",
        error.message
      );
    }
  }

  async getLanguageDistribution(classNum: number): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(
        `
        SELECT 
          l.language AS name,
          ROUND(COALESCE(COUNT(cq.id), 0) * 100.0 / NULLIF((SELECT COUNT(cq2.id) FROM createquestion cq2 JOIN language l2 ON cq2.language = l2.id WHERE cq2.is_active = true AND cq2.class = ?), 0), 2) AS value
        FROM language l
        LEFT JOIN createquestion cq
          ON cq.language = l.id 
          AND cq.is_active = true
          AND cq.class = ?
        GROUP BY l.language
        ORDER BY l.id;
      `,
        [classNum, classNum]
      );

      return new CommonResponse(
        true,
        200,
        "Language distribution fetched successfully",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error retrieving language distribution",
        error.message
      );
    }
  }

  async getDifficultyBreakdown(): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(`
      SELECT 
      c.complexity AS LEVEL,

      COUNT(CASE WHEN cl.class = 'XI' THEN 1 END) AS xi,
      COUNT(CASE WHEN cl.class = 'XII' THEN 1 END) AS xii

      FROM complexity c

     LEFT JOIN createquestion cq
     ON cq.complexity = c.id
     AND cq.is_active = 1

    LEFT JOIN class cl
    ON cq.class = cl.id

    GROUP BY c.id, c.complexity
      ORDER BY c.id;
    `);

      return new CommonResponse(
        true,
        200,
        "Difficulty breakdown fetched",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching difficulty breakdown",
        error.message
      );
    }
  }

  async getSubjectDifficultySplit(classNum: number): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(
        `
        SELECT
          COALESCE(sp.subject, s.subject) AS subject_name,
          SUM(CASE WHEN LOWER(c.complexity) = 'easy' THEN 1 ELSE 0 END) AS easy,
          SUM(CASE WHEN LOWER(c.complexity) = 'moderate' THEN 1 ELSE 0 END) AS moderate,
          SUM(
            CASE
              WHEN LOWER(c.complexity) IN ('hard', 'difficult') THEN 1
              ELSE 0
            END
          ) AS hard,
          COUNT(cq.id) AS total
        FROM createquestion cq
        JOIN subject s ON cq.subject = s.id
        LEFT JOIN subject sp ON s.subject_parent_id = sp.id
        LEFT JOIN complexity c ON cq.complexity = c.id
        WHERE cq.class = ?
          AND cq.is_active = 1
        GROUP BY COALESCE(sp.id, s.id), COALESCE(sp.subject, s.subject)
        ORDER BY total DESC, subject_name ASC;
      `,
        [classNum]
      );

      return new CommonResponse(
        true,
        200,
        "Subject difficulty split fetched successfully",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching subject difficulty split",
        error.message
      );
    }
  }

  async getSubjectsWiseDistribution(classNum: number): Promise<CommonResponse> {
    console.log("ds", classNum);
    try {
      const result = await this.dataSource.query(
        `
      SELECT 
        COALESCE(sp.subject, s.subject) AS name,
        COUNT(cq.id) AS count
        FROM createquestion cq
        JOIN subject s 
        ON cq.subject = s.id
        LEFT JOIN subject sp ON s.subject_parent_id = sp.id
        WHERE cq.class = ?
        GROUP BY 
        COALESCE(sp.id, s.id),
        COALESCE(sp.subject, s.subject)
        ORDER BY count DESC;`,
        [classNum]
      );
      return new CommonResponse(
        true,
        200,
        "Subjects by question count fetched",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching subjects by question count",
        error.message
      );
    }
  }

  async getTopFiveByQuestionCount(classNum: number): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(
        `
      SELECT 
      COALESCE(sp.subject, s.subject) AS NAME,
      COUNT(cq.id) AS COUNT

    FROM createquestion cq

    JOIN  subject s ON cq.subject = s.id
    LEFT JOIN subject sp ON s.subject_parent_id = sp.id

    WHERE cq.class = 3 AND cq.is_active = 1

   GROUP BY  COALESCE(sp.id, s.id), COALESCE(sp.subject, s.subject)

   ORDER BY COUNT DESC,NAME ASC           

   LIMIT 5;`,
        [classNum]
      );
      return new CommonResponse(
        true,
        200,
        "Top five subjects by question count fetched",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching top five subjects by question count",
        error.message
      );
    }
  }

  async getClassComparison(): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(`
     SELECT parent.subject AS SUBJECT,

    SUM(CASE WHEN cq.class = 3 THEN 1 ELSE 0 END) AS xi,
    SUM(CASE WHEN cq.class = 2 THEN 1 ELSE 0 END) AS xii

    FROM createquestion cq JOIN subject s ON cq.subject = s.id

    JOIN subject parent ON parent.id = CASE 
        WHEN s.subject_parent_id = 0 THEN s.id
        ELSE s.subject_parent_id
    END

    GROUP BY parent.subject
    HAVING xi > 0 OR xii > 0
    ORDER BY parent.subject;
    `);

      return new CommonResponse(true, 200, "Class comparison fetched", result);
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching class comparison",
        error.message
      );
    }
  }

  //   async getMonthlyAdditions(): Promise<CommonResponse> {
  //     try {
  //        const result = await this.dataSource.query(`
  //         WITH RECURSIVE months AS (
  //   SELECT DATE_FORMAT(
  //     DATE_SUB(
  //       MAX(
  //         CASE
  //           WHEN timestamp REGEXP '^[0-9]+$'
  //           THEN FROM_UNIXTIME(timestamp)
  //           ELSE STR_TO_DATE(timestamp, '%Y-%m-%d %H:%i:%s')
  //         END
  //       ),
  //       INTERVAL 5 MONTH
  //     ),
  //     '%Y-%m-01'
  //   ) AS first_day
  //   FROM createquestion

  //   UNION ALL

  //   SELECT DATE_ADD(first_day, INTERVAL 1 MONTH)
  //   FROM months
  //   WHERE first_day < (
  //     SELECT DATE_FORMAT(
  //       MAX(
  //         CASE
  //           WHEN timestamp REGEXP '^[0-9]+$'
  //           THEN FROM_UNIXTIME(timestamp)
  //           ELSE STR_TO_DATE(timestamp, '%Y-%m-%d %H:%i:%s')
  //         END
  //       ),
  //       '%Y-%m-01'
  //     )
  //     FROM createquestion
  //   )
  // )

  // SELECT
  //   DATE_FORMAT(m.first_day, '%b') AS month,
  //   COUNT(CASE WHEN cq.class = 3 THEN 1 END) AS xi,
  //   COUNT(CASE WHEN cq.class = 2 THEN 1 END) AS xii
  // FROM months m

  // LEFT JOIN createquestion cq
  //   ON (
  //     CASE
  //       WHEN cq.timestamp REGEXP '^[0-9]+$'
  //       THEN FROM_UNIXTIME(cq.timestamp)
  //       ELSE STR_TO_DATE(cq.timestamp, '%Y-%m-%d %H:%i:%s')
  //     END
  //   ) >= m.first_day

  //   AND (
  //     CASE
  //       WHEN cq.timestamp REGEXP '^[0-9]+$'
  //       THEN FROM_UNIXTIME(cq.timestamp)
  //       ELSE STR_TO_DATE(cq.timestamp, '%Y-%m-%d %H:%i:%s')
  //     END
  //   ) < DATE_ADD(m.first_day, INTERVAL 1 MONTH)

  //   AND cq.is_active = 1

  // GROUP BY m.first_day
  // ORDER BY m.first_day;`);

  //       return new CommonResponse(true, 200, "Monthly additions fetched", result);
  //     } catch (error) {
  //       return new CommonResponse(false, 500, "Error fetching monthly additions", error.message);
  //     }
  //   }

  async getMonthlyAdditions(): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(`
        SELECT 
  DATE_FORMAT(m.first_day, '%b') AS month,
  COUNT(CASE WHEN cq.class = 3 THEN 1 END) AS xi,
  COUNT(CASE WHEN cq.class = 2 THEN 1 END) AS xii
FROM (
  SELECT DATE_SUB(max_dates.max_month, INTERVAL seq.n MONTH) AS first_day
  FROM (
    SELECT DATE_FORMAT(MAX(created_at), '%Y-%m-01') AS max_month
    FROM createquestion
  ) max_dates
  CROSS JOIN (
    SELECT 0 AS n UNION ALL SELECT 1 UNION ALL SELECT 2 UNION ALL SELECT 3 UNION ALL SELECT 4 UNION ALL SELECT 5
  ) seq
) m

LEFT JOIN createquestion cq
  ON cq.created_at >= m.first_day
  AND cq.created_at < DATE_ADD(m.first_day, INTERVAL 1 MONTH)
  AND cq.is_active = 1

GROUP BY m.first_day
ORDER BY m.first_day;`);

      return new CommonResponse(true, 200, "Monthly additions fetched", result);
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching monthly additions",
        error.message
      );
    }
  }

  async getPaperStats(): Promise<CommonResponse> {
    try {
      // TOTAL SET (DISTINCT)
      const totalStats = await this.qpRepo
        .createQueryBuilder("qp")
        .select("qp.mode", "mode")
        .addSelect("COUNT(*)", "totalSet")
        .groupBy("qp.mode")
        .getRawMany();

      // TODAY
      const todayStats = await this.qpRepo
        .createQueryBuilder("qp")
        .select("qp.mode", "mode")
        .addSelect("COUNT(*)", "today")
        .where("DATE(CASE WHEN qp.created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(qp.created_at) ELSE qp.created_at END) = CURDATE()")
        .groupBy("qp.mode")
        .getRawMany();

      // THIS MONTH
      const monthStats = await this.qpRepo
        .createQueryBuilder("qp")
        .select("qp.mode", "mode")
        .addSelect("COUNT(*)", "thisMonth")
        .where("MONTH(CASE WHEN qp.created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(qp.created_at) ELSE qp.created_at END) = MONTH(CURDATE())")
        .andWhere("YEAR(CASE WHEN qp.created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(qp.created_at) ELSE qp.created_at END) = YEAR(CURDATE())")
        .groupBy("qp.mode")
        .getRawMany();

      //  NEW: TOTAL RECORD COUNT (NO DISTINCT)
      const totalRecordStats = await this.qpRepo.query(`
      SELECT 
        COUNT(CASE WHEN mode = 'AI' THEN 1 END) AS ai_total_count,
        COUNT(CASE WHEN mode = 'Manual' THEN 1 END) AS manual_total_count
      FROM question_papers
    `);

      const recordCounts = totalRecordStats[0] || {};

      // Mapper
      const mapStats = (mode: string) => {
        const totalSet = totalStats.find((t) => t.mode === mode)?.totalSet || 0;
        const today = todayStats.find((t) => t.mode === mode)?.today || 0;
        const thisMonth =
          monthStats.find((t) => t.mode === mode)?.thisMonth || 0;

        return {
          totalSet: Number(totalSet),
          today: Number(today),
          thisMonth: Number(thisMonth),
        };
      };

      const data = {
        ai: {
          ...mapStats("AI"),
          totalRecords: Number(recordCounts.ai_total_count || 0),
        },
        manual: {
          ...mapStats("Manual"),
          totalRecords: Number(recordCounts.manual_total_count || 0),
        },
      };

      return new CommonResponse(
        true,
        200,
        "Paper stats fetched successfully",
        data
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching paper stats",
        error.message
      );
    }
  }

  async getPaperTrend(): Promise<CommonResponse> {
    try {
      const result = await this.qpRepo.query(`
      SELECT 
        DATE_FORMAT(month_start, '%b') AS MONTH,
        COALESCE(SUM(CASE WHEN qp.mode = 'AI' THEN 1 END), 0) AS ai,
        COALESCE(SUM(CASE WHEN qp.mode = 'MANUAL' THEN 1 END), 0) AS manual
      FROM (
        SELECT DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 5 MONTH), '%Y-%m-01') AS month_start
        UNION ALL
        SELECT DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 4 MONTH), '%Y-%m-01')
        UNION ALL
        SELECT DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 3 MONTH), '%Y-%m-01')
        UNION ALL
        SELECT DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 2 MONTH), '%Y-%m-01')
        UNION ALL
        SELECT DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01')
        UNION ALL
        SELECT DATE_FORMAT(CURDATE(), '%Y-%m-01')
      ) AS months
      LEFT JOIN question_papers qp
        ON DATE_FORMAT(
          CASE 
            WHEN qp.created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(qp.created_at)
            ELSE qp.created_at
          END, 
          '%Y-%m'
        ) = DATE_FORMAT(months.month_start, '%Y-%m')
      GROUP BY months.month_start
      ORDER BY months.month_start;
    `);
      return new CommonResponse(
        true,
        200,
        "Trend fetched successfully",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching trend",
        error.message
      );
    }
  }

  async getBlueprintSummary(): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(`
        SELECT 
          COALESCE(sp.subject, s.subject) AS subject_name,
          GROUP_CONCAT(DISTINCT c.class ORDER BY c.id SEPARATOR ', ') AS classes,
          COUNT(qpm.id) AS total_blueprints
        FROM question_paper_modal qpm
        JOIN subject s ON qpm.subject = s.id
        LEFT JOIN subject sp ON s.subject_parent_id = sp.id
        JOIN class c ON qpm.class = c.id
        GROUP BY COALESCE(sp.id, s.id), COALESCE(sp.subject, s.subject)
        ORDER BY total_blueprints DESC;
      `);
      return new CommonResponse(true, 200, "Blueprint summary fetched", result);
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching blueprint summary",
        error.message
      );
    }
  }

  async getRecentBlueprints(): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(`
        SELECT 
          qpm.blue_print_name AS name,
          c.class AS class_name,
          qpm.marks,
          COALESCE(sp.subject, s.subject) AS subject_name,
          qpm.created_at AS timestamp
        FROM question_paper_modal qpm
        JOIN class c ON qpm.class = c.id
        JOIN subject s ON qpm.subject = s.id
        LEFT JOIN subject sp ON s.subject_parent_id = sp.id
        ORDER BY (CASE WHEN qpm.created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(qpm.created_at) ELSE qpm.created_at END) DESC
        LIMIT 10;
      `);
      return new CommonResponse(true, 200, "Recent blueprints fetched", result);
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching recent blueprints",
        error.message
      );
    }
  }

  async getOverviewStats(): Promise<CommonResponse> {
    try {

      const queryResult = await this.dataSource.query(
        `
        SELECT 
          (SELECT COUNT(*) FROM createquestion) AS total_questions,
          (SELECT COUNT(*) FROM createquestion WHERE is_active = 1) AS active_questions,
          (SELECT COUNT(*) FROM createquestion WHERE created_at >= DATE_FORMAT(CURDATE(), '%Y-%m-01')) AS questions_this_month,
          (SELECT COUNT(*) FROM question_papers) AS total_papers,
          (SELECT COUNT(*) FROM question_papers WHERE (CASE WHEN created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(created_at) ELSE created_at END) >= DATE_FORMAT(CURDATE(), '%Y-%m-01')) AS papers_this_month,
          (SELECT COUNT(*) FROM question_paper_modal) AS total_blueprints,
          (SELECT COUNT(*) FROM question_paper_modal WHERE (CASE WHEN created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(created_at) ELSE created_at END) >= DATE_FORMAT(CURDATE(), '%Y-%m-01')) AS blueprints_this_month,
          (SELECT COUNT(*) FROM question_papers WHERE mode = 'AI') AS total_ai_papers,
          (SELECT COUNT(*) FROM question_papers WHERE mode = 'Manual') AS total_manual_papers,
          (SELECT COUNT(*) FROM question_papers WHERE mode = 'AI' AND (CASE WHEN created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(created_at) ELSE created_at END) >= DATE_FORMAT(CURDATE(), '%Y-%m-01')) AS ai_papers_this_month,
          (SELECT COUNT(*) FROM question_papers WHERE mode = 'Manual' AND (CASE WHEN created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(created_at) ELSE created_at END) >= DATE_FORMAT(CURDATE(), '%Y-%m-01')) AS manual_papers_this_month,
          (SELECT COUNT(*) FROM ums_authentications) AS total_users,
          (SELECT COUNT(*) FROM ums_authentications WHERE (CASE WHEN created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(created_at) ELSE created_at END) >= DATE_FORMAT(CURDATE(), '%Y-%m-01')) AS users_this_month
      `
      );

      const counts = queryResult[0];

      return new CommonResponse(
        true,
        200,
        "Overview statistics fetched successfully",
        counts
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error retrieving overview statistics",
        error.message
      );
    }
  }

  async getRecentQuestionPapers(): Promise<CommonResponse> {
    try {
      const result = await this.dataSource.query(`
        SELECT 
          s.subject AS name,
          c.class AS className,
          qp.mode AS type,
          qp.setno AS sets,
          GROUP_CONCAT(DISTINCT l.language SEPARATOR ', ') AS languages,
          'Generated' AS status,
          qp.created_at AS timestamp
        FROM question_papers qp
        JOIN question_paper_modal qpm ON qp.qpcode = qpm.id
        JOIN class c ON qpm.class = c.id
        JOIN subject s ON qp.subject = s.id
        JOIN language l ON s.language = l.id
        GROUP BY qp.paper_bundler_id, qp.setno
        ORDER BY (CASE WHEN qp.created_at REGEXP '^[0-9]+$' THEN FROM_UNIXTIME(qp.created_at) ELSE qp.created_at END) DESC
        LIMIT 5;
      `);
      return new CommonResponse(
        true,
        200,
        "Recent question papers fetched",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching recent question papers",
        error.message
      );
    }
  }

  async getQuestionsBySubject(
    classNum: number,
    subjectId?: number
  ): Promise<CommonResponse> {
    try {
      let query = `
        SELECT 
          cq.id,
          cq.question AS question_text,
          COALESCE(sp.subject, s.subject) AS subject_name,
          c.class AS class_name,
          comp.complexity AS difficulty,
          ab.type AS ability,
          abt.types AS ability_type,
          l.language AS language,
          cq.marks,
          cq.is_active AS status,
          cq.created_at AS timestamp
        FROM createquestion cq
        JOIN subject s ON cq.subject = s.id
        LEFT JOIN subject sp ON s.subject_parent_id = sp.id
        JOIN class c ON cq.class = c.id
        LEFT JOIN complexity comp ON cq.complexity = comp.id
        LEFT JOIN abilities ab ON cq.ability = ab.id
        LEFT JOIN ability_types abt ON cq.ability_type = abt.id
        LEFT JOIN language l ON cq.language = l.id
        WHERE cq.class = ?
      `;

      const params: (number | undefined)[] = [classNum];

      if (subjectId) {
        query += ` AND cq.subject = ?`;
        params.push(subjectId);
      }

      query += ` ORDER BY cq.id DESC LIMIT 50`;

      const result = await this.dataSource.query(query, params);
      return new CommonResponse(
        true,
        200,
        "Questions fetched successfully",
        result
      );
    } catch (error) {
      return new CommonResponse(
        false,
        500,
        "Error fetching questions",
        error.message
      );
    }
  }
}
